/**
 * Slide-in drawer for managing blog snippets from inside the blog editor.
 * Triggered by the "⚙ Manage snippets" button in the editor sidebar.
 *
 * Uses the shared renderSnippetList component so the look matches the
 * Settings → Blog Snippets page exactly.
 */
import { client } from "../api/client.js";
import { escapeHtml } from "./escapeHtml.js";
import { toast } from "./toast.js";
import { renderSnippetList } from "./blogSnippetList.js";
import { openBlogSnippetEditor } from "./blogSnippetEditor.js";

let currentDrawer = null;

export async function openBlogSnippetDrawer() {
    closeBlogSnippetDrawer();

    const overlay = document.createElement('div');
    overlay.id = 'blog-snippet-drawer-overlay';
    overlay.className = 'blog-snippet-drawer-overlay';
    overlay.innerHTML = `
        <div class="blog-snippet-drawer" role="dialog" aria-label="Manage blog snippets">
            <div class="bs-drawer-topbar">
                <div>
                    <div class="bs-drawer-title">Blog snippets</div>
                    <div class="bs-drawer-sub">Reusable HTML blocks for your posts.</div>
                </div>
                <div class="bs-drawer-actions">
                    <button class="btn btn-primary btn-sm" id="bs-drawer-new">+ New snippet</button>
                    <button class="btn btn-ghost btn-sm" id="bs-drawer-close">Close</button>
                </div>
            </div>
            <div class="bs-drawer-filters">
                <button class="bs-filter-chip active" data-filter="all">All</button>
                <button class="bs-filter-chip" data-filter="brand">🎨 Brand</button>
                <button class="bs-filter-chip" data-filter="global">🌐 Global</button>
                <button class="bs-filter-chip" data-filter="auto">Auto-apply</button>
                <button class="bs-filter-chip" data-filter="manual">Manual</button>
            </div>
            <div class="bs-drawer-body" id="bs-drawer-body">
                <div class="bs-list-empty">Loading…</div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);
    currentDrawer = overlay;

    let snippets = [];
    let activeFilter = 'all';

    async function refresh() {
        try {
            const res = await client.listBlogSnippets();
            snippets = res?.snippets || [];
            render();
        } catch (err) {
            overlay.querySelector('#bs-drawer-body').innerHTML = '<div class="bs-list-empty">Failed to load snippets.</div>';
        }
    }

    function render() {
        const filtered = snippets.filter((s) => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'brand') return s.scope === 'brand';
            if (activeFilter === 'global') return s.scope === 'global';
            if (activeFilter === 'auto') return s.auto_apply;
            if (activeFilter === 'manual') return !s.auto_apply;
            return true;
        });
        overlay.querySelector('#bs-drawer-body').innerHTML = renderSnippetList(filtered, { compact: false });
    }

    // Filter chips
    overlay.querySelectorAll('.bs-filter-chip').forEach((btn) => {
        btn.addEventListener('click', () => {
            overlay.querySelectorAll('.bs-filter-chip').forEach((b) => b.classList.remove('active'));
            btn.classList.add('active');
            activeFilter = btn.dataset.filter;
            render();
        });
    });

    // Row action delegation
    overlay.querySelector('#bs-drawer-body').addEventListener('click', async (e) => {
        const actBtn = e.target.closest('[data-snippet-action]');
        if (!actBtn) return;
        const id = actBtn.dataset.snippetId;
        const action = actBtn.dataset.snippetAction;
        const snip = snippets.find((s) => s.id === id);
        if (!snip) return;

        if (action === 'edit') {
            openBlogSnippetEditor({ snippet: snip, onSaved: refresh });
            return;
        }
        if (action === 'toggle-auto') {
            try {
                await client.updateBlogSnippet(id, { auto_apply: !snip.auto_apply });
                refresh();
            } catch (err) { toast('error', 'Failed to update'); }
            return;
        }
        if (action === 'delete') {
            if (!confirm(`Delete snippet "${snip.name}"? This cannot be undone.`)) return;
            try {
                await client.deleteBlogSnippet(id);
                toast('success', 'Snippet deleted');
                refresh();
            } catch (err) { toast('error', 'Delete failed'); }
            return;
        }
    });

    overlay.querySelector('#bs-drawer-close').addEventListener('click', closeBlogSnippetDrawer);
    overlay.querySelector('#bs-drawer-new').addEventListener('click', () => {
        openBlogSnippetEditor({ onSaved: refresh });
    });
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeBlogSnippetDrawer();
    });
    const esc = (e) => { if (e.key === 'Escape') { closeBlogSnippetDrawer(); document.removeEventListener('keydown', esc); } };
    document.addEventListener('keydown', esc);

    refresh();
}

export function closeBlogSnippetDrawer() {
    if (currentDrawer) {
        currentDrawer.remove();
        currentDrawer = null;
    } else {
        const existing = document.getElementById('blog-snippet-drawer-overlay');
        if (existing) existing.remove();
    }
}
